import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';

import { cn } from '@/ux/utils';

import { cleanupLinkPreviews, renderLinkPreviews } from '../components/LinkPreviewRenderer';
import { ContentViewerNavigationPortal } from './ContentViewerNavigationPortal';
import { ReadOnlyContentGrid } from './ReadOnlyContentGrid';
import { RefreshableImage } from './RefreshableImage';
import { ScrollProgressBar } from './ScrollProgressBar';
import { VideoGridPreview } from './VideoGridPreview';
import { VideoProxyPlayer } from './VideoProxyPlayer';

interface ContentViewerViewportProps {
  content: string;
  className?: string;
  showNavigation?: boolean;
  showProgressBar?: boolean;
  hideNativeScrollbar?: boolean;
}

export const ContentViewerViewport: React.FC<ContentViewerViewportProps> = ({
  content,
  className,
  showNavigation = true,
  showProgressBar = true,
  hideNativeScrollbar = true,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Check if content is empty or just contains empty HTML tags
  const isEmpty = !content || content.trim() === '' || content === '<p></p>' || content === '<p><br></p>';

  // Handle link preview and video rendering in preview mode
  useEffect(() => {
    if (!contentRef.current || isEmpty) return;

    // Process content to add link preview containers and handle videos
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;

    // Handle video-wrapper elements from CustomVideo
    const videoWrappers = tempDiv.querySelectorAll('.video-wrapper');
    videoWrappers.forEach((wrapper) => {
      // Find the video embed div within the wrapper
      const embedDiv = wrapper.querySelector('div[data-video-embed]');
      const linkElement = wrapper.querySelector('a[href*="/view?id="]');

      if (embedDiv) {
        const contentId = embedDiv.getAttribute('data-content-id');
        const title = embedDiv.getAttribute('data-title');

        if (contentId) {
          // Create a container for the video proxy player
          const videoContainer = document.createElement('div');
          videoContainer.className = 'video-proxy-container';
          videoContainer.setAttribute('data-content-id', contentId);
          videoContainer.setAttribute('data-title', title || '   ');

          // Replace the entire wrapper with the video container
          wrapper.parentNode?.replaceChild(videoContainer, wrapper);
        }
      }
    });

    // Handle standalone video links (not inside video-wrapper)
    const videoLinks = tempDiv.querySelectorAll('a[href*="/view?id="]');
    videoLinks.forEach((link) => {
      // Skip if already processed as part of video-wrapper
      if (link.closest('.video-wrapper')) {
        return;
      }

      const href = link.getAttribute('href') || '';
      const match = href.match(/\/view\?id=([^&]+)/);
      if (match) {
        const contentId = match[1];
        const title = link.textContent || '   ';

        // Create a container for the video proxy player
        const videoContainer = document.createElement('div');
        videoContainer.className = 'video-proxy-container';
        videoContainer.setAttribute('data-content-id', contentId);
        videoContainer.setAttribute('data-title', title);

        // Hide the original link
        const linkElement = link as HTMLElement;
        linkElement.style.display = 'none';
        linkElement.classList.add('group-[.hide-some]:hidden');

        // Insert video container after the link's parent
        const parent = link.closest('p');
        if (parent && parent.parentNode) {
          parent.parentNode.insertBefore(videoContainer, parent.nextSibling);
        }
      }
    });

    // Handle standalone video embed divs (not inside video-wrapper)
    const videoEmbeds = tempDiv.querySelectorAll('div[data-video-embed]');
    videoEmbeds.forEach((embed) => {
      // Skip if already processed as part of video-wrapper
      if (embed.closest('.video-wrapper')) {
        return;
      }

      const contentId = embed.getAttribute('data-content-id');
      const title = embed.getAttribute('data-title');

      if (contentId) {
        // Create a container for the video proxy player
        const videoContainer = document.createElement('div');
        videoContainer.className = 'video-proxy-container';
        videoContainer.setAttribute('data-content-id', contentId);
        videoContainer.setAttribute('data-title', title || '   ');

        // Replace the embed with the container
        embed.parentNode?.replaceChild(videoContainer, embed);
      }
    });

    // Find all links that should show preview
    const links = tempDiv.querySelectorAll('a[href]');
    links.forEach((link) => {
      const href = link.getAttribute('href');
      const text = link.textContent?.trim();

      // Show preview for:
      // 1. Standalone links (where link text === href)
      // 2. Product/asset links (/ecommerce?productId=)
      const isStandaloneLink = href && text && href === text;
      const isProductLink = href && href.includes('/ecommerce?productId=');
      const isVideoLink = href && href.includes('/view?id=');

      if ((isStandaloneLink || isProductLink) && !isVideoLink) {
        // Add a preview container after the link's parent paragraph
        const parent = link.closest('p');
        if (parent && parent.parentNode) {
          const previewContainer = document.createElement('div');
          previewContainer.className = 'link-preview-decoration';
          previewContainer.setAttribute('data-href', href);
          parent.parentNode.insertBefore(previewContainer, parent.nextSibling);
        }
      }
    });

    // Update the content with preview containers
    contentRef.current.innerHTML = tempDiv.innerHTML;

    // Handle content grid elements
    const contentGrids = contentRef.current.querySelectorAll('div[data-type="content-grid"]');
    contentGrids.forEach((grid) => {
      try {
        const cellsData = grid.getAttribute('data-cells');
        const rows = parseInt(grid.getAttribute('data-rows') || '1');
        const columns = parseInt(grid.getAttribute('data-columns') || '1');
        const height = parseInt(grid.getAttribute('data-height') || '400');
        const columnSizesData = grid.getAttribute('data-column-sizes');
        const rowSizesData = grid.getAttribute('data-row-sizes');

        const cells = cellsData ? JSON.parse(cellsData) : [];
        const columnSizes = columnSizesData ? JSON.parse(columnSizesData) : undefined;
        const rowSizes = rowSizesData ? JSON.parse(rowSizesData) : undefined;

        // Create container for ReadOnlyContentGrid
        const container = document.createElement('div');
        container.className = 'content-grid-read-only-container my-6';

        // Replace grid with container
        grid.parentNode?.replaceChild(container, grid);

        // Render ReadOnlyContentGrid
        const root = ReactDOM.createRoot(container);
        root.render(
          <ReadOnlyContentGrid
            cells={cells}
            rows={rows}
            columns={columns}
            columnSizes={columnSizes}
            rowSizes={rowSizes}
            height={height}
          />,
        );
      } catch (e) {
        console.error('Error rendering content grid:', e);
      }
    });

    // Replace img tags with RefreshableImage components
    // Skip images inside content-grid cells as they need special styling
    const images = contentRef.current.querySelectorAll('img');
    images.forEach((img) => {
      // Skip if image is inside a content grid cell
      if (img.closest('.content-grid-cell')) {
        return;
      }
      const src = img.getAttribute('src');
      const alt = img.getAttribute('alt') || '';
      const width = img.getAttribute('width') || undefined;
      const height = img.getAttribute('height') || undefined;
      const style = img.getAttribute('style') || '';
      const className = img.className;
      const dataStretch = img.getAttribute('data-stretch') || undefined;

      // Parse style string to object
      const styleObj: Record<string, string> = {};
      if (style) {
        style.split(';').forEach((rule) => {
          const [key, value] = rule.split(':').map((s) => s.trim());
          if (key && value) {
            // Convert kebab-case to camelCase
            const camelKey = key.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
            styleObj[camelKey] = value;
          }
        });
      }

      // Create container for RefreshableImage - use span to maintain inline flow
      const container = document.createElement('span');
      container.className = 'refreshable-image-container';

      // Check if image is stretched
      const isStretched = dataStretch === 'true';

      // Set container style based on stretch
      if (isStretched) {
        container.style.display = 'block';
        container.style.width = '100%';
        container.setAttribute('data-stretch', 'true');
      } else {
        container.style.display = 'block';
        container.style.textAlign = 'center';
      }

      // Replace img with container
      img.parentNode?.replaceChild(container, img);

      // Render RefreshableImage
      const root = ReactDOM.createRoot(container);
      root.render(
        <RefreshableImage
          src={src || ''}
          alt={alt}
          width={width}
          height={height}
          style={styleObj}
          className={className}
          showLoadingIndicator={true}
          data-stretch={dataStretch}
        />,
      );
    });

    // Render video proxy players
    const videoContainers = contentRef.current.querySelectorAll('.video-proxy-container');
    videoContainers.forEach((container) => {
      const contentId = container.getAttribute('data-content-id');
      const title = container.getAttribute('data-title');

      if (contentId) {
        const root = ReactDOM.createRoot(container as HTMLElement);
        root.render(<VideoProxyPlayer contentId={contentId} title={title || undefined} isPreview={true} />);
      }
    });

    // Handle videos inside content grid cells
    const gridVideoEmbeds = contentRef.current.querySelectorAll('.content-grid-video-embed');
    gridVideoEmbeds.forEach((embed) => {
      const contentId = embed.getAttribute('data-content-id');
      const title = embed.getAttribute('data-title');

      if (contentId) {
        const root = ReactDOM.createRoot(embed as HTMLElement);
        root.render(
          <VideoGridPreview contentId={contentId} title={title || undefined} className="w-full h-full" />,
        );
      }
    });

    // Handle fallback video tags in content grids
    const gridVideos = contentRef.current.querySelectorAll(
      '.content-grid-cell video[data-grid-video="true"]',
    );
    gridVideos.forEach((video) => {
      const contentId = video.getAttribute('data-content-id');
      const src = video.getAttribute('src');

      if (contentId || src) {
        // Create a container for the video proxy player
        const videoContainer = document.createElement('div');
        videoContainer.className = 'content-grid-video-container';
        videoContainer.style.width = '100%';
        videoContainer.style.height = '100%';

        // Replace video with container
        video.parentNode?.replaceChild(videoContainer, video);

        const root = ReactDOM.createRoot(videoContainer);
        root.render(
          <VideoGridPreview
            contentId={contentId || undefined}
            src={src || undefined}
            className="w-full h-full"
          />,
        );
      }
    });

    // Render link previews with clickable links (isEditable = false)
    setTimeout(() => {
      if (contentRef.current) {
        renderLinkPreviews(contentRef.current, false);
      }
    }, 100);

    return () => {
      if (contentRef.current) {
        // Clean up RefreshableImage components
        const imageContainers = contentRef.current.querySelectorAll('.refreshable-image-container');
        imageContainers.forEach((container) => {
          const root = (container as any)._reactRootContainer;
          if (root) {
            root.unmount();
          }
        });

        // Clean up video proxy players
        const videoContainers = contentRef.current.querySelectorAll('.video-proxy-container');
        videoContainers.forEach((container) => {
          const root = (container as any)._reactRootContainer;
          if (root) {
            root.unmount();
          }
        });

        // Clean up grid video embeds
        const gridVideoEmbeds = contentRef.current.querySelectorAll('.content-grid-video-embed');
        gridVideoEmbeds.forEach((container) => {
          const root = (container as any)._reactRootContainer;
          if (root) {
            root.unmount();
          }
        });

        // Clean up grid video containers
        const gridVideoContainers = contentRef.current.querySelectorAll('.content-grid-video-container');
        gridVideoContainers.forEach((container) => {
          const root = (container as any)._reactRootContainer;
          if (root) {
            root.unmount();
          }
        });

        // Clean up content grid containers
        const gridContainers = contentRef.current.querySelectorAll('.content-grid-read-only-container');
        gridContainers.forEach((container) => {
          const root = (container as any)._reactRootContainer;
          if (root) {
            root.unmount();
          }
        });

        cleanupLinkPreviews(contentRef.current);
      }
    };
  }, [content, isEmpty]);

  // Add global styles to hide scrollbars (only if hideNativeScrollbar is true)
  useEffect(() => {
    if (!hideNativeScrollbar) return;

    const style = document.createElement('style');
    style.textContent = `
      body {
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
      }
      body::-webkit-scrollbar {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
      }
      html {
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
      }
      html::-webkit-scrollbar {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, [hideNativeScrollbar]);

  return (
    <div className="relative h-full flex flex-col">
      {showProgressBar && <ScrollProgressBar containerRef={scrollContainerRef} />}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto"
        style={{
          ...(hideNativeScrollbar
            ? {
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }
            : {
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
              }),
        }}
      >
        <div
          ref={contentRef}
          className={cn(
            'content-viewer',
            // Base prose classes with dark mode
            'prose prose-lg sm:prose-xl lg:prose-2xl dark:prose-invert',
            // Layout
            'max-w-4xl mx-auto leading-relaxed tracking-wide',
            'bg-transparent min-h-full',
            // Custom prose modifiers for proper rendering
            'prose-headings:font-bold prose-headings:text-foreground',
            'prose-h1:text-4xl prose-h1:mb-6',
            'prose-h2:text-3xl prose-h2:mb-6',
            'prose-h3:text-2xl prose-h3:mb-6',
            'prose-p:text-foreground prose-p:mb-6',
            'prose-a:text-primary hover:prose-a:underline',
            'prose-strong:font-bold prose-strong:text-foreground',
            'prose-em:italic',
            'prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:my-6',
            'prose-code:bg-muted prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none',
            'prose-pre:bg-muted prose-pre:overflow-x-auto prose-pre:my-6',
            'prose-ul:list-disc prose-ul:pl-6 prose-ul:my-6',
            'prose-ol:list-decimal prose-ol:pl-6 prose-ol:my-6',
            'prose-img:rounded-lg prose-img:shadow-sm prose-img:my-6',
            'prose-hr:border-border prose-hr:my-6',
            // Mobile responsive padding
            'p-4 sm:p-8 md:p-12 lg:p-16',
            // Hide video links
            '[&_a[href*="/view?id="]]:hidden',
            className,
          )}
        >
          {isEmpty ? (
            <div className="text-muted-foreground text-center py-12">
              <p className="text-lg">No content to preview</p>
              <p className="text-sm mt-2">Start writing in the editor to see the preview</p>
            </div>
          ) : null}
        </div>
      </div>
      {showNavigation && <ContentViewerNavigationPortal contentRef={contentRef} useViewport={true} />}
    </div>
  );
};
